import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "./use-supabase-user";
import { toast } from "sonner";
import { useCallback } from "react";

/**
 * Secure form draft hook that stores sensitive candidate data in the database
 * instead of localStorage, with automatic 7-day cleanup
 * 
 * @param formType - Type of form (e.g., 'candidate', 'first_interview', 'second_interview', etc.)
 * @param candidateId - Optional candidate ID for linking drafts to specific candidates
 */
export const useCandidateDraft = (formType: string, candidateId?: string) => {
  const { user } = useSupabaseUser();
  const queryClient = useQueryClient();

  const queryKey = ["candidateDraft", formType, candidateId || "new"];

  // Fetch existing draft
  const { data: draft, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user) return null;

      const query = supabase
        .from("candidate_form_drafts")
        .select("*")
        .eq("user_id", user.id)
        .eq("form_type", formType);

      if (candidateId) {
        query.eq("candidate_id", candidateId);
      } else {
        query.is("candidate_id", null);
      }

      const { data, error } = await query.maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching draft:", error);
        throw error;
      }

      return data;
    },
    enabled: !!user,
    staleTime: 30_000, // 30 seconds
  });

  // Save draft mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (formData: Record<string, any>) => {
      if (!user) throw new Error("User not authenticated");

      const draftData = {
        user_id: user.id,
        form_type: formType,
        candidate_id: candidateId || null,
        form_data: formData,
      };

      const { data, error } = await supabase
        .from("candidate_form_drafts")
        .upsert(draftData, {
          onConflict: "user_id,form_type,candidate_id",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: any) => {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    },
  });

  // Delete draft mutation
  const deleteDraftMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");

      const query = supabase
        .from("candidate_form_drafts")
        .delete()
        .eq("user_id", user.id)
        .eq("form_type", formType);

      if (candidateId) {
        query.eq("candidate_id", candidateId);
      } else {
        query.is("candidate_id", null);
      }

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: any) => {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
    },
  });

  // Convenience methods
  const saveDraft = useCallback(
    (formData: Record<string, any>) => {
      return saveDraftMutation.mutateAsync(formData);
    },
    [saveDraftMutation]
  );

  const deleteDraft = useCallback(() => {
    return deleteDraftMutation.mutateAsync();
  }, [deleteDraftMutation]);

  return {
    draft: (draft?.form_data as any) || null,
    draftExists: !!draft,
    draftUpdatedAt: draft?.updated_at,
    isLoading,
    saveDraft,
    deleteDraft,
    isSaving: saveDraftMutation.isPending,
    isDeleting: deleteDraftMutation.isPending,
  };
};
